import { Component, Input, OnInit, ViewChild, ElementRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { EbwModalService } from '@ebw-monorepo/modal';
import moment from 'moment';
import { lastValueFrom } from 'rxjs';
import { ControlliService } from '../../../../services/controlli.service';
import { EsitoValidazione, PrelievoInput, SuperamentoInput } from '../../../../models/controlli.model';
import { ControlliCodiciPrelieviDialogComponent } from '../controlli-codici-prelievi-dialog/controlli-codici-prelievi-dialog.component';
import { ControlliCodiciSuperamentiDialogComponent } from '../controlli-codici-superamenti-dialog/controlli-codici-superamenti-dialog.component';
import { ControlliTemplateInfoDialogComponent } from '../controlli-template-info-dialog/controlli-template-info-dialog.component';

interface RowWithOutcome {
  riga: number;
  valido: boolean;
  errori: string[];
  data: PrelievoInput | SuperamentoInput;
}

/**
 * Reusable upload/validate/save section for PSA "Controlli" data.
 * Behaviour (endpoints called, columns shown) is driven by `type`.
 */
@Component({
  selector: 'ebw-monorepo-controlli-upload-sezione',
  templateUrl: './controlli-upload-sezione.component.html',
  styleUrls: ['./controlli-upload-sezione.component.scss']
})
export class ControlliUploadSezioneComponent implements OnInit {
  @Input() type: 'prelievi' | 'superamenti';

  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  // the paginator exists only while there are rows: it is attached again every time the table is shown
  @ViewChild(MatPaginator) set paginator(paginator: MatPaginator) {
    this.dataSource.paginator = paginator;
  }

  // only the current page is rendered: thousands of rows (each with its hidden errors row) freeze the page
  readonly dataSource = new MatTableDataSource<RowWithOutcome>([]);
  readonly pageSizeOptions = [25, 50, 100];

  private _rows: RowWithOutcome[] = [];

  get rows(): RowWithOutcome[] {
    return this._rows;
  }

  set rows(rows: RowWithOutcome[]) {
    this._rows = rows;
    this.dataSource.data = rows;
  }


  validating = false;
  saving = false;
  fileName: string | null = null;

  constructor(
    private _controlliService: ControlliService,
    private _modalService: EbwModalService,
    private _dialog: MatDialog
  ) { }

  expandedRows = new Set<number>();

  ngOnInit(): void { }

  get columns(): string[] {
    return this.type === 'prelievi'
      ? ['riga', 'uuid', 'dataOraPrelievo', 'codicePrelievo', 'codiceParametro', 'valore', 'unitaMisura', 'esito']
      : ['riga', 'uuid', 'dataOraPrelievo', 'codiceParametro', 'tipologiaPuntoDiConformita', 'superamento', 'esito'];
  }

  get validRowCount(): number {
    return this.rows.filter((r) => r.valido).length;
  }

  isInvalidRow = (_index: number, row: RowWithOutcome): boolean => !row.valido;

  toggleRow(row: RowWithOutcome): void {
    if (row.valido) {
      return;
    }
    if (this.expandedRows.has(row.riga)) {
      this.expandedRows.delete(row.riga);
    } else {
      this.expandedRows.add(row.riga);
    }
  }

  // valoreRapportoProva puo' essere assente quando il valore e' sotto il limite di quantificazione (LOQ)
  formatValore(row: RowWithOutcome): string {
    const data = row.data as PrelievoInput;
    const haValore = data.valoreRapportoProva !== undefined && data.valoreRapportoProva !== null && (data.valoreRapportoProva as any) !== '';
    if (haValore) {
      return String(data.valoreRapportoProva);
    }
    if (data.valoreLOQ) {
      const loqBlank = data.LOQ === undefined || data.LOQ === null || (data.LOQ as any) === '';
      return loqBlank ? `${data.valoreLOQ} (valore LOQ mancante)` : `${data.valoreLOQ} ${data.LOQ}`;
    }
    return '-';
  }

  getUnitaMisura(row: RowWithOutcome): string {
    return (row.data as PrelievoInput).unitaMisura ?? '';
  }

  formatData(valore: any): string {
    if (!valore) return '';
    const data = moment(valore);
    return data.isValid() ? data.format('DD/MM/YYYY [alle] HH:mm:ss') : String(valore);
  }

  formatBooleano(valore: any): string {
    if (typeof valore === 'boolean') return valore ? 'Sì' : 'No';
    if (typeof valore === 'number') return valore !== 0 ? 'Sì' : 'No';
    if (typeof valore === 'string') {
      return ['true', 'si', 'sì', '1', 'x'].includes(valore.trim().toLowerCase()) ? 'Sì' : 'No';
    }
    return '-';
  }

  openCodiciAmmessi(): void {
    if (this.type === 'prelievi') {
      this._dialog.open(ControlliCodiciPrelieviDialogComponent, { maxWidth: '100vw', maxHeight: '100%', width: '70%', hasBackdrop: false });
    } else {
      this._dialog.open(ControlliCodiciSuperamentiDialogComponent, { maxWidth: '100vw', maxHeight: '100%', width: '70%', hasBackdrop: false });
    }
  }

  openTemplateInfo(): void {
    this._dialog.open(ControlliTemplateInfoDialogComponent, {
      maxWidth: '100vw',
      maxHeight: '100%',
      width: '70%',
      hasBackdrop: false,
      data: { type: this.type }
    });
  }

  cleanInput(event: Event): void {
    (event.target as HTMLInputElement).value = '';
  }

  /** Clears the loaded file and its validation outcome, back to the empty drop zone */
  reset(): void {
    this.rows = [];
    this.fileName = null;
    this.expandedRows.clear();
  }

  async upload(event: Event | DragEvent): Promise<void> {
    const files = (event.target as HTMLInputElement)?.files || (event as DragEvent).dataTransfer?.files;
    if (!files || !files.length) {
      return;
    }
    const file = files[0];
    this.reset();
    this.fileName = file.name;
    this.validating = true;

    try {
      const response =
        this.type === 'prelievi'
          ? await lastValueFrom(this._controlliService.validatePrelievi(file))
          : await lastValueFrom(this._controlliService.validateSuperamenti(file));

      this.rows = response.esiti.map((esito: EsitoValidazione, i: number) => ({
        riga: esito.riga,
        valido: esito.valido,
        errori: esito.errori,
        data: response.righe[i]
      }));
    } catch (error: any) {
      this.reset();
      this._modalService.openErrorModal({
        title: 'Errore validazione file',
        content: error?.error?.message ?? 'Errore durante la validazione del file'
      });
    } finally {
      this.validating = false;
    }
  }

  async save(): Promise<void> {
    const validRows = this.rows.filter((r) => r.valido).map((r) => r.data);
    if (!validRows.length) {
      this._modalService.openWarningModal({ title: 'Salvataggio', content: 'Nessuna riga valida da salvare' });
      return;
    }

    const invalidCount = this.rows.length - validRows.length;
    const validSingolare = validRows.length === 1;
    const invalidSingolare = invalidCount === 1;

    const soggettoValide = validSingolare ? "l'unica riga" : `le ${validRows.length} righe`;
    const verboValide = validSingolare ? 'ha' : 'hanno';
    const soggettoNonValide = invalidSingolare ? "L'unica riga" : `Le ${invalidCount} righe`;
    const aggettivoNonConforme = invalidSingolare ? 'non conforme' : 'non conformi';
    const evidenziata = invalidSingolare ? 'evidenziata' : 'evidenziate';
    const esclusa = invalidSingolare ? 'sarà esclusa' : 'saranno escluse';

    const messaggioConferma =
      invalidCount > 0
        ? `Il salvataggio riguarderà esclusivamente ${soggettoValide} che ${verboValide} superato la validazione. ${soggettoNonValide} ${aggettivoNonConforme}, ${evidenziata} in rosso, ${esclusa}. Confermare?`
        : `Confermi il salvataggio ${validSingolare ? "dell'unica riga" : `delle ${validRows.length} righe`}?`;

    const confermato = await lastValueFrom(
      this._modalService.openConfirmModal({ title: 'Conferma salvataggio', content: messaggioConferma }).afterClosed()
    );
    if (!confermato) {
      return;
    }

    this.saving = true;
    try {
      const response =
        this.type === 'prelievi'
          ? await lastValueFrom(this._controlliService.savePrelievi(validRows as PrelievoInput[]))
          : await lastValueFrom(this._controlliService.saveSuperamenti(validRows as SuperamentoInput[]));

      this._modalService.openInfoModal({ title: 'Salvataggio', content: response.message });
      this.reset();
    } catch (error: any) {
      this._modalService.openErrorModal({
        title: 'Errore salvataggio',
        content: error?.error?.message ?? 'Errore durante il salvataggio'
      });
    } finally {
      this.saving = false;
    }
  }
}
